import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  MessageSquare, Send, ArrowLeft, MapPin, Calendar, Loader2, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function ChatPage() {
  const { api, user } = useAuth();
  const isLargeScreen = useIsLargeScreen();
  const walkthroughId = !isLargeScreen
    ? (user?.role === 'manager' ? 'wt-mgr-messages' : 'wt-worker-messages')
    : undefined;
  const [searchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deletingMsg, setDeletingMsg] = useState(null);
  const [hidingThread, setHidingThread] = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const paramShiftId = searchParams.get('shiftId');
  const paramWorkerId = searchParams.get('workerId');

  const fetchThreads = useCallback(async () => {
    try {
      const res = await api.get('/chat/threads');
      setThreads(res.data);
      return res.data;
    } catch {
      toast.error('Failed to load conversations');
      return [];
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchMessages = useCallback(async (threadId) => {
    try {
      const res = await api.get(`/chat/threads/${threadId}/messages`);
      setMessages(res.data);
    } catch {}
  }, [api]);

  useEffect(() => {
    const init = async () => {
      const loadedThreads = await fetchThreads();
      if (paramShiftId && paramWorkerId) {
        try {
          const wId = paramWorkerId === 'self' ? user?.id : paramWorkerId;
          const res = await api.post(`/chat/threads?shift_id=${paramShiftId}&worker_id=${wId}`);
          setActiveThread(res.data);
          fetchMessages(res.data.id);
          await fetchThreads();
        } catch (err) {
          console.error('Failed to open thread:', err);
        }
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeThread) {
      fetchMessages(activeThread.id);
      pollRef.current = setInterval(() => fetchMessages(activeThread.id), 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [activeThread, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !activeThread) return;
    setSending(true);
    try {
      await api.post(`/chat/threads/${activeThread.id}/messages`, { messageText: newMessage });
      setNewMessage('');
      fetchMessages(activeThread.id);
      fetchThreads();
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    setDeletingMsg(msgId);
    try {
      await api.delete(`/chat/messages/${msgId}`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    } finally {
      setDeletingMsg(null);
    }
  };

  const openThread = (thread) => {
    setActiveThread(thread);
    fetchMessages(thread.id);
  };

  const handleHideThread = async (e, threadId) => {
    e.stopPropagation();
    setHidingThread(threadId);
    try {
      await api.post(`/chat/threads/${threadId}/hide`);
      setThreads(prev => prev.filter(t => t.id !== threadId));
      toast.success('Conversation removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove conversation');
    } finally {
      setHidingThread(null);
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }

  if (activeThread) {
    const otherName = activeThread.otherUser?.fullName || 'User';
    const shiftLabel = activeThread.shift ? `${activeThread.shift.branchName} - ${activeThread.shift.date}` : 'Shift';

    return (
      <div className="flex flex-col h-[calc(100vh-80px)]" data-testid="chat-thread-view">
        <div className="p-4 border-b bg-white flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setActiveThread(null)} className="rounded-full h-9 w-9 p-0" data-testid="chat-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{otherName}</h3>
            <p className="text-xs text-slate-500 truncate">{shiftLabel}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F1F5F9]">
          {messages.length === 0 ? (
            <div className="text-center text-slate-400 py-12">
              <MessageSquare className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map(msg => {
              const isMe = msg.senderId === user?.id;
              return (
                <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'} group`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 relative ${isMe ? 'bg-[#0f1b3d] text-white rounded-br-md' : 'bg-white text-slate-900 shadow-sm rounded-bl-md'}`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.messageText}</p>
                    <div className={`flex items-center gap-2 mt-1 ${isMe ? 'justify-end' : ''}`}>
                      <p className={`text-xs ${isMe ? 'text-slate-300' : 'text-slate-400'}`}>{formatTime(msg.createdAt)}</p>
                      {isMe && (
                        <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          disabled={deletingMsg === msg.id}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-msg-${msg.id}`}
                        >
                          {deletingMsg === msg.id 
                            ? <Loader2 className="h-3 w-3 animate-spin text-slate-300" />
                            : <Trash2 className="h-3 w-3 text-slate-300 hover:text-red-400" />
                          }
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Type a message..."
              className="h-12 rounded-xl bg-slate-50"
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={sending || !newMessage.trim()}
              className="h-12 w-12 rounded-xl bg-[#0f1b3d] hover:bg-[#1a2d5a] shrink-0"
              data-testid="chat-send-btn"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="chat-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-walkthrough={walkthroughId}>Messages</h1>
        <p className="text-slate-500 mt-1 text-base">Chat with {user?.role === 'manager' ? 'applicants about your shifts' : 'managers about your shifts'}.</p>
        {/* Phase 6 UX: set expectations so urgent matters aren't left in chat alone. */}
        <p className="text-xs text-slate-400 mt-1" data-testid="chat-support-hint">
          For urgent operational issues, contact your {user?.role === 'manager' ? 'worker' : 'manager'} directly or email support@shiftcover365.co.uk (usually replies within 3–5 business days during the pilot).
        </p>
      </div>

      {threads.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center" data-testid="chat-empty-state">
            <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No conversations yet</h3>
            <p className="text-slate-500">
              {user?.role === 'manager'
                ? 'Start a conversation from an applicant\u2019s card on the Available workers screen.'
                : 'Start a conversation from your Waiting or Confirmed shifts.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {threads.map(t => (
            <Card
              key={t.id}
              className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-all ${t.unreadCount > 0 ? 'bg-blue-50/50' : ''}`}
              onClick={() => openThread(t)}
              data-testid={`chat-thread-${t.id}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-[#0f1b3d] flex items-center justify-center text-white font-bold shrink-0">
                  {t.otherUser?.fullName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold text-slate-900 truncate">{t.otherUser?.fullName || 'Unknown'}</h4>
                    {t.lastMessage && <span className="text-xs text-slate-400 shrink-0">{formatDate(t.lastMessage.createdAt)}</span>}
                  </div>
                  <p className="text-sm text-slate-500 truncate">
                    {t.shift?.branchName} - {t.shift?.date}
                  </p>
                  {t.lastMessage && (
                    <p className="text-sm text-slate-400 truncate mt-0.5">
                      {t.lastMessage.senderId === user?.id ? 'You: ' : ''}{t.lastMessage.messageText}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {t.unreadCount > 0 && (
                    <Badge className="bg-[#DA291C] text-white h-6 min-w-[24px] flex items-center justify-center rounded-full text-xs">
                      {t.unreadCount}
                    </Badge>
                  )}
                  <button
                    onClick={(e) => handleHideThread(e, t.id)}
                    disabled={hidingThread === t.id}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                    data-testid={`hide-thread-${t.id}`}
                    title="Remove conversation"
                  >
                    {hidingThread === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
